<template>
  <div v-if="icon" :class="classList">
    <input
      :type="type"
      :name="name"
      :placeholder="placeholder"
      @focus="focusIn"
      @focusout="focusOut"
      ref="input"
      tabindex="0"
      :value="value"
      @input="update" />
    <div class="a-input__clear" v-if="value && clearButton === true" @click="clear()">
      <icon name="times" />
    </div>
    <button
      class="a-input__icon"
      :class="{ 'a-input__icon--disabled': buttonDisabled }"
      @click="focusIn"
      :disabled="buttonDisabled">
      <slot />
    </button>
  </div>
  <div :class="classList" v-else-if="type === 'text' || type === 'hidden' || type === 'password'">
    <input
      :type="type"
      :name="name"
      :placeholder="placeholder"
      @focus="focusIn"
      @focusout="focusOut"
      tabindex="0"
      :value="value"
      ref="input"
      @input="update" />
  </div>
  <div :class="classList" v-else-if="type === 'date'">
    <input
      type="date"
      :name="name"
      :placeholder="placeholder"
      @focus="focusIn"
      @focusout="focusOut"
      tabindex="0"
      :value="value"
      ref="input"
      @input="update"
      :min="min"
      :max="max" />
  </div>
  <div :class="classList"  v-else-if="type === 'textarea'">
    <textarea
      :type="type"
      :name="name"
      :style="`min-height: ${minHeight}px`"
      :placeholder="placeholder"
      @focus="focusIn"
      @focusout="focusOut"
      tabindex="0"
      :value="value"
      ref="input"
      @input="update"></textarea>
  </div>
</template>

<script>
export default {
  name: 'v-input',
  props: {
    type: {
      type: [String],
      required: true,
    },
    icon: {
      type: [Boolean],
      default: false,
    },
    buttonDisabled: {
      type: [Boolean],
      default: false,
    },
    fluid: {
      type: [Boolean],
      default: true,
    },
    clearButton: {
      type: [Boolean],
      default: false,
    },
    minHeight: {
      type: [Number],
      default: 75,
    },
    value: [String],
    subtype: [String],
    size: [String],
    placeholder: [String],
    name: [String],
    min: [String],
    max: [String],
  },
  data() {
    return {
      focus: false,
      inputValue: null,
    };
  },
  computed: {
    classList() {
      const {
        fluid, focus, type, icon, subtype, size,
      } = this;
      return {
        'a-input': true,
        'a-input--fluid': fluid,
        'a-input--focus': focus,
        'a-input--icon': icon,
        [`a-input--${type}`]: type,
        [`a-input--${subtype}`]: subtype,
        [`a-input--${size}`]: size,
      };
    },
  },
  methods: {
    focusIn() {
      this.focus = true;
      if (this.$refs.input) {
        this.$refs.input.focus();
      }
    },
    focusOut() {
      this.focus = false;
    },
    update() {
      this.inputValue = this.$refs.input.value;
      this.$emit('input', this.inputValue);
    },
    clear() {
      this.$refs.input.value = '';
      this.update();
    },
  },
  watchers: {
    inputValue(value) {
      this.$emit('input', value);
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '../styles/component-utils';

  $parent: '.a-input';

  $iconWidth: 45px + $spacing-small;

  #{$parent} {
    background: $signable-secondary-4-shade-3;
    border: 1px solid $color-border;
    border-radius: 3px;
    float: left;
    transition: border .25s ease, box-shadow .25s ease;
    input, textarea {
      padding: $spacing-small;
      border: 0;
      background: transparent;
      outline: none;
      font-size: inherit;
      color: inherit;
      font-family: inherit;
      line-height: 1.45;
    }
    textarea {
      display: block;
      min-width: 100%;
      max-width: 100%;
    }
    //modifiers
    &--textarea {
      width: 100%;
    }
    &--fluid {
      width: 100%;
      input {
        width: 100%;
      }
    }
    &--small {
      input {
        padding: $spacing-xs;
      }
    }
    &--icon {
      position: relative;
      input {
        width: 100%;
        padding-right: $iconWidth;
      }
      #{$parent}__icon {
        position: absolute;
        right: 0;
        top: 0;
        line-height: 0;
        border: 1px solid $color-border;
        padding: $spacing-base;
        margin: -1px -1px -1px 0px;
        border-top-right-radius: 3px;
        border-bottom-right-radius: 3px;
        transition: border .25s ease, color .25s ease;
        input {
          float: left;
        }
        svg {
          height: 13px;
          width: 13px;
        }
        &--disabled {
          background: $signable-secondary-4-shade-3;
          color: $signable-secondary-4;
          &:hover,
          &:active {
            cursor: not-allowed;
            background: $signable-secondary-4-shade-3;
            color: $signable-secondary-4-shade-1;
          }
        }
      }
      #{$parent}__clear {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: 60px;
        cursor: pointer;
        color: $signable-secondary-3-shade-1;
        transition: color .25s ease;
        &:hover {
          color: $signable-secondary-3;
        }
        svg {
          vertical-align: text-top;
        }
      }
    }
    &--focus {
      border-color: $signable-primary-1;
      box-shadow: 0px 0px 10px -3px $signable-primary-1;
      transition: border .25s ease, box-shadow .25s ease, color .25s ease;
      position: relative;
      #{$parent}__icon {
        border-color: $signable-primary-1;
        transition: border .25s ease, color .25s ease;
        svg {
          color: $signable-primary-1;
          transition: color .25s ease;
        }
        &--disabled {
          background: $signable-secondary-4-shade-3;
          color: $signable-secondary-4;
          &:hover,
          &:active {
            cursor: not-allowed;
            background: $signable-secondary-4-shade-3;
            color: $signable-secondary-4-shade-1;
          }
        }
      }
    }
    &--error {
      border-color: $color-error;
      #{$parent}__icon {
        border-color: $color-error;
        transition: border .25s ease, color .25s ease;
        svg {
          color: $color-error;
          transition: color .25s ease;
        }
      }
      &#{$parent}--focus {
        box-shadow: 0px 0px 10px -3px $color-error;
      }
    }
  }
</style>
